import React from "react";
import { DialogTitle, Stack, IconButton, Typography, Box } from "@mui/material";
import ModalLayout from "./Layout";
import ModalLayoutContent from "./Layout/Content";
import ModalLayoutFooter from "./Layout/Footer";
import ModalLayoutBtn from "./Layout/Btn";
import { GitBranch, X } from "react-feather";
import ModalLayoutInput from "./Layout/Input";
import { CircularProgress } from "@mui/material";

/**
 *
 * @param {{
 *  isShown: boolean;
 *  onHide: () => void;
 *  onAction: () => void;
 *  creating: boolean;
 *  newBranchName: string;
 *  setNewBranchName: (e: React.ChangeEvent) => void;
 *  activeBranch: string;
 * }} data
 */
const CreateBranchModal = ({
  isShown,
  onHide,
  onAction,
  creating,
  newBranchName,
  setNewBranchName,
  activeBranch
}) => {
  return (
    <ModalLayout 
      isShown={isShown} 
      onHide={onHide}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "12px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          overflow: "hidden",
          background: "linear-gradient(to bottom, #ffffff, #f8f9fa)",
          border: "1px solid rgba(0,0,0,0.08)",
          transition: "all 0.3s ease-in-out"
        }
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        position={"relative"}
        padding={"16px 24px"}
        borderBottom={"1px solid rgba(0,0,0,0.05)"}
        sx={{ background: "rgba(235,245,255,0.6)" }}
      >
        <DialogTitle
          padding={"0 !important"}
          sx={{
            color: "#3f51b5",
            fontWeight: 800,
            fontSize: "11px",
            display: "flex",
            alignItems: "center",
            "::before": {
              content: '""',
              display: "inline-block",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "#3f51b5",
              marginRight: "10px",
              animation: "pulse 1.5s infinite",
              "@keyframes pulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(63, 81, 181, 0.7)" },
                "70%": { boxShadow: "0 0 0 10px rgba(63, 81, 181, 0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(63, 81, 181, 0)" }
              }
            }
          }}
        >
          <GitBranch size={14} style={{ marginRight: "6px" }} />
          Create New Branch
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onHide}
          sx={{
            position: "absolute",
            right: 16,
            top: 10,
            color: "#495057",
            "&:hover": {
              background: "rgba(0,0,0,0.05)",
              transform: "rotate(90deg)",
              transition: "transform 0.3s ease"
            }
          }}
        >
          <X size={16} />
        </IconButton>
      </Stack>
      <ModalLayoutContent sx={{ padding: "24px 28px" }}>
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center",
          gap: "20px"
        }}>
          {activeBranch === 'main' && (
            <Typography
              component={"span"}
              sx={{
                fontSize: "11px",
                color: "#0c5460",
                background: "rgba(209,236,241,0.6)",
                display: "block",
                padding: "12px 16px",
                borderRadius: "6px",
                border: "1px solid #bee5eb",
                textAlign: "center",
                lineHeight: 1.6,
                animation: "fadeIn 1.1s ease-in",
                "@keyframes fadeIn": {
                  "0%": { opacity: 0, transform: "translateY(-5px)" },
                  "50%": { opacity: 0.5, transform: "translateY(-5px)" },
                  "100%": { opacity: 1, transform: "translateY(0)" }
                }
                }}
            >
              Editing a file in the <Typography component="span" fontWeight={700} fontSize="11px">main</Typography> branch is not allowed. <br/>
              Create a new branch, or switch to a different branch to make changes.
              <Typography component="span" display="block" fontSize="11px" marginTop="6px" fontStyle="italic">
                Don't worry, your files will remain in the Editor when switching branch.
              </Typography>
            </Typography>
          )}
          
          <Box sx={{ width: "100%" }}>
            <ModalLayoutInput
              id="branch-name-input"
              type="text"
              placeholder="Enter branch name"
              value={newBranchName}
              onChange={setNewBranchName}
              sx={{
                fontSize: "11px",
                borderRadius: "8px",
                border: "1px solid rgba(0,0,0,0.1)",
                padding: "10px 16px",
                width: "100%",
                transition: "all 0.2s",
                "&:focus": {
                  borderColor: "#7986cb",
                  boxShadow: "0 0 0 3px rgba(121,134,203,0.15)"
                }
              }}
            />
          </Box>
        </Box>
      </ModalLayoutContent>
      <ModalLayoutFooter sx={{ padding: "16px 24px" }}>
        <ModalLayoutBtn
          color="primary"
          disabled={creating}
          onClick={onAction}
          form="create-branch-form"
          type="submit"
          variant="contained"
          sx={{
            fontSize: "11px",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            padding: "8px 20px",
            transition: "all 0.2s",
            background: "linear-gradient(45deg, #3f51b5, #5c6bc0)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(63,81,181,0.3)"
            },
            "&:disabled": {
              background: "#b0bec5",
              color: "#ffffff"
            }
          }}
        >
          {creating ? (
            <>
              Creating branch...&nbsp;
              <CircularProgress size={16} color="inherit" />
            </>
          ) : (
            <>
              <GitBranch size={16} style={{ marginRight: "6px" }} />
              Create Branch
            </>
          )}
        </ModalLayoutBtn>
      </ModalLayoutFooter>
    </ModalLayout>
  );
};

export default CreateBranchModal;
